import './avatar.css';
import { t, tText, onLang } from './i18n';
import {
  AVATAR_CATEGORIES,
  paintAvatar,
  randomAvatar,
  type AvatarConfig,
} from '../avatar';

/**
 * Modal character creator for the profile avatar. Cycles each feature with
 * ◀ ▶ (colour categories also show a swatch strip), live-previews the upper-body
 * portrait, and reports the result via onSave. App owns persistence.
 */
export interface AvatarEditorCallbacks {
  onSave(config: AvatarConfig): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export class AvatarEditor {
  private readonly overlay = el('div', 'avatar-overlay');
  private readonly preview = el('div', 'avatar-preview');
  private readonly cb: AvatarEditorCallbacks;
  private cfg: AvatarConfig;
  private readonly rows: { update: () => void }[] = [];

  constructor(root: HTMLElement, cb: AvatarEditorCallbacks, initial: AvatarConfig) {
    this.cb = cb;
    this.cfg = { ...initial };

    const panel = el('div', 'avatar-panel interactive');
    const title = el('div', 'avatar-title');
    tText(title, 'avatar.title');

    const body = el('div', 'avatar-body');
    const left = el('div', 'avatar-left');
    left.appendChild(this.preview);
    const dice = el('button', 'avatar-dice');
    dice.textContent = '🎲';
    dice.title = t('avatar.randomize');
    onLang(() => (dice.title = t('avatar.randomize')));
    dice.onclick = () => {
      this.cfg = randomAvatar();
      this.repaint();
    };
    left.appendChild(dice);

    const controls = el('div', 'avatar-controls');
    for (const cat of AVATAR_CATEGORIES) controls.appendChild(this.buildRow(cat));

    body.append(left, controls);

    const footer = el('div', 'avatar-footer');
    const cancel = el('button', 'avatar-btn');
    tText(cancel, 'avatar.cancel');
    cancel.onclick = () => this.close();
    const save = el('button', 'avatar-btn avatar-save');
    tText(save, 'avatar.save');
    save.onclick = () => {
      this.cb.onSave({ ...this.cfg });
      this.close();
    };
    footer.append(cancel, save);

    panel.append(title, body, footer);
    this.overlay.appendChild(panel);
    this.overlay.addEventListener('pointerdown', (e) => {
      if (e.target === this.overlay) this.close(); // click backdrop = cancel
    });
    this.overlay.style.display = 'none';
    root.appendChild(this.overlay);
  }

  /** One control row: label, ◀ value ▶, and (for colours) a swatch strip. */
  private buildRow(cat: (typeof AVATAR_CATEGORIES)[number]): HTMLElement {
    const row = el('div', 'avatar-row');
    const label = el('span', 'avatar-lbl');
    tText(label, cat.label);

    const set = (v: number) => {
      this.cfg[cat.key] = ((v % cat.count) + cat.count) % cat.count;
      this.repaint();
    };
    const prev = el('button', 'avatar-arrow');
    prev.textContent = '◀';
    prev.onclick = () => set(this.cfg[cat.key] - 1);
    const next = el('button', 'avatar-arrow');
    next.textContent = '▶';
    next.onclick = () => set(this.cfg[cat.key] + 1);
    const val = el('span', 'avatar-val');

    const cycler = el('div', 'avatar-cycler');
    cycler.append(prev, val, next);

    let swatches: HTMLElement | null = null;
    if ('swatch' in cat && cat.swatch) {
      swatches = el('div', 'avatar-swatches');
      cat.swatch.forEach((hex, i) => {
        const sw = el('button', 'avatar-sw');
        sw.style.background = hex;
        sw.onclick = () => set(i);
        swatches!.appendChild(sw);
      });
    }

    const update = () => {
      const cur = this.cfg[cat.key];
      val.textContent = `${cur + 1}/${cat.count}`;
      if (swatches) {
        Array.from(swatches.children).forEach((c, i) => c.classList.toggle('on', i === cur));
      }
    };
    this.rows.push({ update });

    row.append(label, cycler);
    if (swatches) row.appendChild(swatches);
    return row;
  }

  private repaint(): void {
    paintAvatar(this.preview, this.cfg);
    for (const r of this.rows) r.update();
  }

  open(config: AvatarConfig): void {
    this.cfg = { ...config };
    this.repaint();
    this.overlay.style.display = 'flex';
  }

  close(): void {
    this.overlay.style.display = 'none';
  }
}
